import React, { useState } from 'react';
import ModalBody from '../../ModalBody';
import CustomTextInput from '../../../CustomTextInput';
import { useTranslation } from 'react-i18next';
import { WasteCode } from '../../../../services/apiSLTP/materials/wasteCodes';
import { crudStatus } from '../../../../utils/enums';
import { useCreateWasteCodeMutation, useUpdateWasteCodeMutation } from '../../../../hooks/mutations/useWasteCodeMutation';
import ToggleLine from '../../../ToggleLine';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { Box, Chip, Typography } from '@mui/material';

interface ModalProps {
    wasteCode: WasteCode | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateWasteCodeModal: React.FC<ModalProps> = ({ wasteCode, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();
    const [selectedDesignation, setSelectedDesignation] = useState(wasteCode?.designation ?? '');
    const [selectedDescription, setSelectedDescription] = useState(wasteCode?.description ?? '');
    const [selectedCrudStatus, setSelectedCrudStatus] = useState(wasteCode?.crudStatus ?? crudStatus.OK);

    const { mutate: mutateWasteCode } = useUpdateWasteCodeMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating waste code:', error.message),
    });

    const { mutate: addWasteCode } = useCreateWasteCodeMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error creating waste code:', error.message),
    });

    const handleActiveChange = (isActive: boolean) => {
        setSelectedCrudStatus(isActive ? crudStatus.OK : crudStatus.INACTIVE);
    };

    const isDangerous = selectedDesignation.endsWith('*');

    return (
        <ModalBody
            title={t('wasteCode')}
            subTitle={isEditMode ? t('update') : t('add')}
            closeText={t('cancel')}
            okText={t('validate')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disabledOk={!selectedDesignation.length || !selectedDescription.length}
            handleOk={() => {
                isEditMode
                    ? mutateWasteCode({
                          wasteCodeId: wasteCode?._id || '',
                          data: { designation: selectedDesignation, description: selectedDescription, crudStatus: selectedCrudStatus },
                      })
                    : addWasteCode({ designation: selectedDesignation, description: selectedDescription });
            }}
        >
            <CustomTextInput
                value={selectedDesignation}
                label={t('designation')}
                onChange={setSelectedDesignation}
                errorText={t('error')}
                required
                placeholder="17 01 01 ou 17 05 04*"
            />

            {isDangerous && (
                <Box sx={{ mb: 2 }}>
                    <Chip label={t('dangerousWaste')} color="error" size="small" icon={<Typography>⚠️</Typography>} />
                </Box>
            )}

            <CustomTextInput
                value={selectedDescription}
                label={t('description')}
                onChange={setSelectedDescription}
                errorText={t('error')}
                required
                multiline
                placeholder="Ex: Béton, Terres et cailloux..."
            />

            <ToggleLine checked={selectedCrudStatus === crudStatus.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
        </ModalBody>
    );
};

export default UpdateWasteCodeModal;
